import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Box, HStack, Stack, Text, Badge, Button, IconButton, Alert, AlertIcon, Spinner, VisuallyHidden,
  FormControl, FormLabel, Select, Input, Checkbox, Table, Thead, Tbody, Tr, Th, Td, useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  fetchGapGroups, fetchGapEntries, triageGaps, gapErrorText,
} from "../../services/agentGapsService";
import {
  CATEGORY_WORDS, STATUS_WORDS, AREA_WORDS, VERDICTS,
  categoryWords, reasonWords, statusWords, areaWords, agentTypeWords, reasonHelp, whoWords,
} from "./gapWords";
import AssignModal from "../../components/messaging/AssignModal";
import { SourceTaskChip, useSourceTasks } from "./liveActivity/issueTasks";

// What clinic agents couldn't do, across all clinics, grouped by category, reason, area and action,
// for everyone the SuperAdmin page admits to filter and triage
// (docs/superpowers/specs/2026-10-15-agent-gaps-design.md). A refusal shows the server's sentence,
// or gapErrorText's words for a code. Every text from the server is rendered as plain text.
//
// Each group says whether its reason is usually by design, a fix, or one to check (VERDICTS), and
// an open one says what happened and what to do (reasonHelp). A group, or one of its entries, can
// be assigned as a Messages task, as the Live Activity rows are: the task points back with
// source_kind "agent_gap" and source_ref the group key or the entry's id, and the row shows it.
// Beside it, Resolve marks what isn't an issue, or works as designed (wontfix, "Not an issue"): a
// group as of the groups on screen, as "Mark whole group" does, or one entry.

const CATEGORIES = Object.keys(CATEGORY_WORDS);
const STATUSES = Object.keys(STATUS_WORDS);
const AREAS = Object.keys(AREA_WORDS);
const ROLES = [["doctor", "Doctor"], ["admin", "Admin"], ["superadmin", "Superadmin"]];
const ROLE_WORDS = Object.fromEntries(ROLES);
// The triage choices, the usual one first; New reopens an entry.
const MARKS = ["reviewed", "fixed", "wontfix", "new"];
const TYPING_PAUSE_MS = 400;
const NOTE_MAX = 1000;
// The server groups at most this many of the newest matching entries (the API contract).
const GROUPING_CAP = 50000;

const CATEGORY_COLORS = {
  no_access: "orange", agent_settings: "purple", cant_do_yet: "blue", blocked: "yellow", failed: "red",
};
const STATUS_COLORS = { new: "orange", reviewed: "blue", fixed: "green", wontfix: "gray" };
const MUTED = { color: "gray.600", _dark: { color: "gray.400" } };

const NO_FILTERS = {
  category: [], area: "", status: "", role: "", action: "", schema: "", since: "", until: "",
};

// The filters as the server takes them: no empty values, categories in the spec's order.
function serverFilters(filters) {
  const out = {};
  const category = CATEGORIES.filter((code) => filters.category.includes(code));
  if (category.length) out.category = category;
  ["area", "status", "role", "action", "schema", "since", "until"].forEach((key) => {
    const value = String(filters[key] || "").trim();
    if (value) out[key] = value;
  });
  return out;
}

// "2026-10-15 18:40 UTC", or to the second.
function formatTime(iso, { seconds = false } = {}) {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return "-";
  return `${date.toISOString().slice(0, seconds ? 19 : 16).replace("T", " ")} UTC`;
}

const roleWords = (code) => {
  if (!code) return "-";
  return Object.prototype.hasOwnProperty.call(ROLE_WORDS, code) ? ROLE_WORDS[code] : String(code);
};
const userWords = (name) => name || "Unknown user";
const SOURCE_KIND = "agent_gap";
// What Resolve marks a gap as: not an issue, or working as designed.
const RESOLVED = "wontfix";
const resolvedWords = (count) => `Resolved ${count} ${count === 1 ? "entry" : "entries"} as not an issue.`;
// A run's id as an entry shows it: its first characters, the whole in its title.
const shortId = (id) => String(id || "").slice(0, 8);

// "Seen 14 times by 6 users, last 2026-10-15 18:40 UTC."
function seenWords(group) {
  const count = Number(group.count) || 0;
  const users = Number(group.users) || 0;
  return `Seen ${count} ${count === 1 ? "time" : "times"} by ${users} ${users === 1 ? "user" : "users"}, `
    + `last ${formatTime(group.last_seen)}.`;
}

// The task a gap becomes: a title, and a description with everything a developer needs to decide
// and fix it (plain text: the Assign window shows it as written). `entry`: one of the group's
// entries, assigned on its own.
function gapTaskText(group, entry = null) {
  const help = reasonHelp(group.reason);
  const verdict = VERDICTS[help.verdict] || VERDICTS.check;
  const what = `${reasonWords(group.reason)}${group.action ? ` (${group.action})` : ""}`;
  const lines = [
    `${categoryWords(group.category)}: ${reasonWords(group.reason)}`,
    `Area: ${areaWords(group.area)} · Action: ${group.action || "-"}`,
  ];
  if (entry) {
    const who = whoWords(entry.who);
    lines.push(
      `When: ${formatTime(entry.created_at, { seconds: true })} · Clinic: ${entry.schema || "-"} · `
        + `Role: ${roleWords(entry.role)} · Agent type: ${agentTypeWords(entry.agent_type)} · `
        + `User: ${userWords(entry.user_name)}`,
      `Detail: ${entry.detail || "none recorded"}`,
    );
    if (who) lines.push(`Whose access: ${who}`);
  }
  lines.push(
    "",
    `What happened: ${help.means}`,
    `Bug or by design? ${verdict.label}. ${verdict.says}`,
    `What to do: ${help.todo}`,
    "",
  );
  if (entry) {
    lines.push(`Run: ${entry.run_id || "-"} · Agent: ${entry.agent_id || "-"} · Entry: ${entry.id}`);
  } else {
    lines.push(seenWords(group));
    const details = (group.details || []).filter(Boolean);
    if (details.length) lines.push("Details:", ...details.map((detail) => `- ${detail}`));
    lines.push(`Gap group: ${group.group_key}`);
  }
  lines.push("(SuperAdmin, Compliance & Monitoring, Agent gaps)");
  return {
    title: `Agent gap: ${what}${entry ? `, ${formatTime(entry.created_at)}` : ""}`,
    description: lines.join("\n"),
  };
}

// A row's task, or the button that assigns one. `name`: what the button says it assigns.
function TaskCell({ task, testId, name, canAssign, onAssign }) {
  if (task) return <SourceTaskChip task={task} testId={testId} />;
  return (
    <Button
      size="xs"
      variant="outline"
      aria-label={`Assign a task: ${name}`}
      isDisabled={!canAssign}
      onClick={(e) => { e.stopPropagation(); onAssign(); }}
    >
      Assign task
    </Button>
  );
}

// Marks what isn't an issue, or works as designed: `onResolve` resolves (a promise), and the button
// is off until it has.
function ResolveButton({ name, onResolve }) {
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const resolve = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await onResolve();
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  return (
    <Button
      size="xs"
      variant="outline"
      colorScheme="green"
      aria-label={`Resolve as not an issue: ${name}`}
      title="Not an issue, or working as designed"
      isDisabled={busy}
      onClick={resolve}
    >
      Resolve
    </Button>
  );
}

// A row's actions: its task (or Assign task), and Resolve unless it is resolved already.
function GapActions({ task, testId, name, canAssign, onAssign, resolved, onResolve }) {
  return (
    <Stack direction="row" spacing={2} align="center" flexWrap="wrap">
      <TaskCell task={task} testId={testId} name={name} canAssign={canAssign} onAssign={onAssign} />
      {!resolved && <ResolveButton name={name} onResolve={onResolve} />}
    </Stack>
  );
}

// What an open group's reason means, whether it is usually a bug, and what to do about it.
function AboutGap({ group }) {
  const help = reasonHelp(group.reason);
  const verdict = VERDICTS[help.verdict] || VERDICTS.check;
  return (
    <Box
      role="region"
      aria-label="About this gap"
      mb={3}
      p={3}
      borderRadius="md"
      borderWidth="1px"
      bg="white"
      _dark={{ bg: "blackAlpha.300" }}
    >
      <Text fontSize="sm"><Text as="b">What happened: </Text>{help.means}</Text>
      <Text fontSize="sm" mt={1}>
        <Text as="b">Bug or by design? </Text>
        <Badge colorScheme={verdict.color} mr={1}>{verdict.label}</Badge>
        {verdict.says}
      </Text>
      <Text fontSize="sm" mt={1}><Text as="b">What to do: </Text>{help.todo}</Text>
    </Box>
  );
}

function FilterSelect({ label, value, onChange, anyLabel, options }) {
  return (
    <FormControl w={{ base: "100%", md: "auto" }}>
      <FormLabel fontSize="xs" mb={1} {...MUTED}>{label}</FormLabel>
      <Select size="sm" value={value} onChange={onChange} minW="150px">
        <option value="">{anyLabel}</option>
        {options.map(([code, words]) => <option key={code} value={code}>{words}</option>)}
      </Select>
    </FormControl>
  );
}

function FilterInput({ label, ...props }) {
  return (
    <FormControl w={{ base: "100%", md: "auto" }}>
      <FormLabel fontSize="xs" mb={1} {...MUTED}>{label}</FormLabel>
      <Input size="sm" {...props} />
    </FormControl>
  );
}

function StatusMix({ statuses }) {
  const shown = STATUSES.filter((code) => Number(statuses?.[code]) > 0);
  if (!shown.length) return <Text fontSize="sm" {...MUTED}>-</Text>;
  return (
    <HStack spacing={1} wrap="wrap">
      {shown.map((code) => (
        <Badge key={code} colorScheme={STATUS_COLORS[code]} variant="subtle">
          {Number(statuses[code])} {statusWords(code).toLowerCase()}
        </Badge>
      ))}
    </HStack>
  );
}

// One group's entries, newest first, 100 a page, with their triage. `asOf` is when the groups on
// screen were read (the server's read_at): marking the whole group covers only the gaps recorded
// by then. A new `refreshes` count (the Refresh button) reads the entries again.
function GroupEntries({ group, filters, asOf, refreshes, onMarked, taskByRef, canAssign, onAssign }) {
  const toast = useToast();
  const groupKey = group.group_key;
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [mark, setMark] = useState(MARKS[0]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  // Only the latest read may land: an older answer (other filters, an earlier page) is dropped.
  const latest = useRef(0);

  const load = useCallback(async (pageNo) => {
    const mine = ++latest.current;
    setLoading(true);
    setError("");
    try {
      const data = await fetchGapEntries(groupKey, filters, pageNo);
      if (mine !== latest.current) return;
      const rows = Array.isArray(data?.entries) ? data.entries : [];
      if (pageNo === 1) {
        setEntries(rows);
        setSelected(new Set());
      } else {
        // A gap recorded since the last page shifts the pages: skip what is already shown.
        setEntries((shown) => {
          const ids = new Set(shown.map((row) => row.id));
          return [...shown, ...rows.filter((row) => !ids.has(row.id))];
        });
      }
      setPage(pageNo);
      setHasMore(Boolean(data?.has_more));
    } catch (err) {
      if (mine === latest.current) setError(gapErrorText(err));
    } finally {
      if (mine === latest.current) setLoading(false);
    }
  }, [groupKey, filters]);

  useEffect(() => { load(1); }, [load, refreshes]);

  const ids = entries.filter((row) => selected.has(row.id)).map((row) => row.id);
  const allSelected = entries.length > 0 && ids.length === entries.length;
  const toggle = (id) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(entries.map((row) => row.id)));

  // Resolve: this entry alone, then the entries and the groups read again. A refusal says why.
  const resolveEntry = async (row) => {
    try {
      const res = await triageGaps({ ids: [row.id], status: RESOLVED });
      toast({ title: resolvedWords(Number(res?.updated) || 0), status: "success", duration: 4000, isClosable: true });
      onMarked();
      load(1);
    } catch (err) {
      toast({ title: gapErrorText(err), status: "error", duration: 6000, isClosable: true });
    }
  };

  const markAs = async (target) => {
    const text = note.trim();
    setSaving(true);
    setSaveError("");
    try {
      const res = await triageGaps({ ...target, status: mark, ...(text ? { note: text } : {}) });
      const count = Number(res?.updated) || 0;
      toast({
        title: `Marked ${count} ${count === 1 ? "entry" : "entries"} as ${statusWords(mark).toLowerCase()}.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setNote("");
      onMarked();
      load(1);
    } catch (err) {
      setSaveError(gapErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box mx={2} p={3} borderWidth="1px" borderRadius="md" bg="gray.50" _dark={{ bg: "whiteAlpha.100" }}>
      <AboutGap group={group} />
      {error && (
        <Alert status="error" borderRadius="md" mb={2}><AlertIcon /><Text fontSize="sm">{error}</Text></Alert>
      )}
      {loading && !entries.length && !error && <Spinner size="sm" />}
      {!loading && !error && !entries.length && (
        <Text fontSize="sm" {...MUTED}>No entries in this group under these filters.</Text>
      )}
      {entries.length > 0 && (
        <Box overflowX="auto">
          <Table size="sm" variant="simple" aria-label="Entries in this group">
            <Thead>
              <Tr>
                <Th px={2}>
                  <Checkbox
                    aria-label="Select all entries"
                    isChecked={allSelected}
                    isIndeterminate={ids.length > 0 && !allSelected}
                    onChange={toggleAll}
                  />
                </Th>
                <Th>Time</Th>
                <Th>Clinic</Th>
                <Th>Role</Th>
                <Th>Agent type</Th>
                <Th>User</Th>
                <Th>Detail</Th>
                <Th>Status</Th>
                <Th>Task</Th>
              </Tr>
            </Thead>
            <Tbody>
              {entries.map((row) => (
                <Tr key={row.id}>
                  <Td px={2}>
                    <Checkbox
                      aria-label={
                        `Select the entry of ${formatTime(row.created_at, { seconds: true })} by ${userWords(row.user_name)}`
                      }
                      isChecked={selected.has(row.id)}
                      onChange={() => toggle(row.id)}
                    />
                  </Td>
                  <Td whiteSpace="nowrap">{formatTime(row.created_at)}</Td>
                  <Td>{row.schema || "-"}</Td>
                  <Td>{roleWords(row.role)}</Td>
                  <Td>{agentTypeWords(row.agent_type)}</Td>
                  <Td>{userWords(row.user_name)}</Td>
                  <Td maxW="320px">
                    <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">{row.detail || "-"}</Text>
                    {whoWords(row.who) && (
                      <Text fontSize="xs" mt={1} {...MUTED}>{`Whose access: ${whoWords(row.who)}`}</Text>
                    )}
                    {row.run_id && (
                      <Text fontSize="xs" mt={1} fontFamily="mono" title={row.run_id} {...MUTED}>
                        {`Run ${shortId(row.run_id)}`}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={STATUS_COLORS[row.status] || "gray"}>{statusWords(row.status)}</Badge>
                    {row.note && (
                      <Text fontSize="xs" mt={1} whiteSpace="pre-wrap" wordBreak="break-word" {...MUTED}>
                        {row.note}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <GapActions
                      task={taskByRef[row.id]}
                      testId={`gap-task-${row.id}`}
                      name={`the entry of ${formatTime(row.created_at, { seconds: true })} by ${userWords(row.user_name)}`}
                      canAssign={canAssign}
                      onAssign={() => onAssign(group, row)}
                      resolved={row.status === RESOLVED}
                      onResolve={() => resolveEntry(row)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      {hasMore && (
        <Button size="sm" mt={2} onClick={() => load(page + 1)} isLoading={loading}>Load more</Button>
      )}
      {entries.length > 0 && (
        <Stack direction={{ base: "column", md: "row" }} spacing={3} mt={3} align={{ md: "flex-end" }}>
          <FormControl w={{ base: "100%", md: "160px" }}>
            <FormLabel fontSize="xs" mb={1} {...MUTED}>Mark as</FormLabel>
            <Select size="sm" value={mark} onChange={(e) => setMark(e.target.value)}>
              {MARKS.map((code) => <option key={code} value={code}>{statusWords(code)}</option>)}
            </Select>
          </FormControl>
          <FormControl flex="1">
            <FormLabel fontSize="xs" mb={1} {...MUTED}>Note</FormLabel>
            <Input
              size="sm"
              maxLength={NOTE_MAX}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional: what was done, or why not"
            />
          </FormControl>
          {/* Off while a mark is on its way, and while the entries are read again (what is
              selected may be about to change). */}
          <Button
            size="sm"
            colorScheme="blue"
            isDisabled={saving || loading || !ids.length}
            onClick={() => markAs({ ids })}
          >
            Mark selected ({ids.length})
          </Button>
          <Button
            size="sm"
            variant="outline"
            isDisabled={saving || loading}
            onClick={() => markAs({ group_key: groupKey, filters, as_of: asOf })}
          >
            Mark whole group ({Number(group.count) || 0})
          </Button>
        </Stack>
      )}
      {saveError && (
        <Alert status="error" borderRadius="md" mt={2}><AlertIcon /><Text fontSize="sm">{saveError}</Text></Alert>
      )}
    </Box>
  );
}

// The words that say which group an expand button opens.
const groupWords = (group) => [reasonWords(group.reason), group.action].filter(Boolean).join(", ");

const GroupsTable = React.memo(function GroupsTable({
  groups, openKey, onToggle, filters, asOf, refreshes, onMarked, taskByRef, canAssign, onAssign, onResolve,
}) {
  return (
    <Box overflowX="auto">
      <Table size="sm" variant="simple" aria-label="Agent gap groups">
        <Thead>
          <Tr>
            <Th px={1}><VisuallyHidden>Open</VisuallyHidden></Th>
            <Th>Category</Th>
            <Th>Area</Th>
            <Th>Action</Th>
            <Th>Reason</Th>
            <Th isNumeric>Count</Th>
            <Th isNumeric>Users</Th>
            <Th>Last seen</Th>
            <Th>Status</Th>
            <Th>Task</Th>
          </Tr>
        </Thead>
        <Tbody>
          {groups.map((group) => {
            const open = group.group_key === openKey;
            const toggle = () => onToggle(open ? null : group.group_key);
            const verdict = VERDICTS[reasonHelp(group.reason).verdict] || VERDICTS.check;
            return (
              <React.Fragment key={group.group_key}>
                <Tr cursor="pointer" onClick={toggle} _hover={{ bg: "gray.50", _dark: { bg: "whiteAlpha.100" } }}>
                  <Td px={1}>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label={`${open ? "Hide" : "Show"} entries: ${groupWords(group)}`}
                      aria-expanded={open}
                      icon={open ? <ChevronDownIcon /> : <ChevronRightIcon />}
                      onClick={(e) => { e.stopPropagation(); toggle(); }}
                    />
                  </Td>
                  <Td>
                    <Badge colorScheme={CATEGORY_COLORS[group.category] || "gray"}>{categoryWords(group.category)}</Badge>
                  </Td>
                  <Td>{areaWords(group.area)}</Td>
                  <Td>
                    {group.action
                      ? <Text as="span" fontFamily="mono" fontSize="xs">{group.action}</Text>
                      : "-"}
                  </Td>
                  <Td maxW="360px">
                    <Text fontSize="sm">{reasonWords(group.reason)}</Text>
                    <Badge colorScheme={verdict.color} variant="outline" fontSize="2xs" mt={1}>{verdict.label}</Badge>
                    {(group.details || []).slice(0, 3).map((detail) => (
                      <Text key={detail} fontSize="xs" noOfLines={2} title={detail} {...MUTED}>{detail}</Text>
                    ))}
                  </Td>
                  <Td isNumeric>{Number(group.count) || 0}</Td>
                  <Td isNumeric>{Number(group.users) || 0}</Td>
                  <Td whiteSpace="nowrap">{formatTime(group.last_seen)}</Td>
                  <Td><StatusMix statuses={group.statuses} /></Td>
                  <Td>
                    <GapActions
                      task={taskByRef[group.group_key]}
                      testId={`gap-task-${group.group_key}`}
                      name={groupWords(group)}
                      canAssign={canAssign}
                      onAssign={() => onAssign(group, null)}
                      resolved={Number(group.count) > 0
                        && Number(group.statuses?.[RESOLVED]) >= Number(group.count)}
                      onResolve={() => onResolve(group)}
                    />
                  </Td>
                </Tr>
                {open && (
                  <Tr>
                    <Td colSpan={10} px={0} py={2}>
                      <GroupEntries
                        group={group}
                        filters={filters}
                        asOf={asOf}
                        refreshes={refreshes}
                        onMarked={onMarked}
                        taskByRef={taskByRef}
                        canAssign={canAssign}
                        onAssign={onAssign}
                      />
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
});

// Mounted while the modal is open, so it reads the groups on every open.
function GapsPanel() {
  const [filters, setFilters] = useState(NO_FILTERS);
  // The typed filters take effect after a pause in typing.
  const [typed, setTyped] = useState({ action: "", schema: "" });
  useEffect(() => {
    if (filters.action === typed.action && filters.schema === typed.schema) return undefined;
    const timer = setTimeout(
      () => setTyped({ action: filters.action, schema: filters.schema }),
      TYPING_PAUSE_MS,
    );
    return () => clearTimeout(timer);
  }, [filters.action, filters.schema, typed.action, typed.schema]);
  const appliedKey = JSON.stringify(serverFilters({ ...filters, ...typed }));
  const applied = useMemo(() => JSON.parse(appliedKey), [appliedKey]);

  const [data, setData] = useState(null);
  // What "Mark whole group" may cover: the server's read time of the groups on screen (read_at),
  // or, in an answer without one, when the browser asked for them.
  const [asOf, setAsOf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openKey, setOpenKey] = useState(null);
  // Counts the Refresh clicks: each one reads the groups, and an open group's entries, again.
  const [refreshes, setRefreshes] = useState(0);
  // Only the latest read may land: an answer for older filters is dropped.
  const latest = useRef(0);
  // The tasks made from gaps (by group key or entry id), the signed-in user who assigns them, and
  // what the Assign window is open for: {group, entry} (entry null for the whole group).
  const { me, taskByRef, reloadTasks } = useSourceTasks(SOURCE_KIND, "Agent gaps");
  const [assigning, setAssigning] = useState(null);
  const assign = useCallback((group, entry) => setAssigning({ group, entry }), []);
  const toast = useToast();
  // Resolve a whole group: what of it was recorded by the groups on screen (asOf), under the
  // filters, as "Mark whole group" marks it; then the groups, and an open group's entries, read
  // again. A refusal says why.
  const resolveGroup = useCallback(async (group) => {
    try {
      const res = await triageGaps({ group_key: group.group_key, filters: applied, as_of: asOf, status: RESOLVED });
      toast({ title: resolvedWords(Number(res?.updated) || 0), status: "success", duration: 4000, isClosable: true });
      setRefreshes((count) => count + 1);
    } catch (err) {
      toast({ title: gapErrorText(err), status: "error", duration: 6000, isClosable: true });
    }
  }, [applied, asOf, toast]);

  const load = useCallback(async () => {
    const mine = ++latest.current;
    const askedAt = new Date().toISOString();
    setLoading(true);
    setError("");
    try {
      const fresh = await fetchGapGroups(applied);
      if (mine === latest.current) {
        setData(fresh || null);
        setAsOf(typeof fresh?.read_at === "string" && fresh.read_at ? fresh.read_at : askedAt);
      }
    } catch (err) {
      if (mine === latest.current) {
        setData(null);
        setError(gapErrorText(err));
      }
    } finally {
      if (mine === latest.current) setLoading(false);
    }
  }, [applied]);

  useEffect(() => { load(); }, [load, refreshes]);

  const setFilter = (key) => (e) => {
    const { value } = e.target;
    setFilters((current) => ({ ...current, [key]: value }));
  };
  const toggleCategory = (code) => setFilters((current) => ({
    ...current,
    category: current.category.includes(code)
      ? current.category.filter((c) => c !== code)
      : [...current.category, code],
  }));

  const groups = Array.isArray(data?.groups) ? data.groups : [];
  const totals = data?.totals || null;

  return (
    <Stack spacing={4}>
      <Text fontSize="sm" {...MUTED}>
        Each time a clinic agent couldn't do something, in every clinic, grouped by what stopped it.
        Click a group to see its entries and mark them.
      </Text>

      <HStack spacing={2} wrap="wrap" role="group" aria-label="Categories">
        {CATEGORIES.map((code) => {
          const on = filters.category.includes(code);
          return (
            <Button
              key={code}
              size="xs"
              aria-pressed={on}
              variant={on ? "solid" : "outline"}
              colorScheme={on ? CATEGORY_COLORS[code] : "gray"}
              onClick={() => toggleCategory(code)}
            >
              {categoryWords(code)}{totals ? ` (${Number(totals[code]) || 0})` : ""}
            </Button>
          );
        })}
      </HStack>

      <Stack direction={{ base: "column", md: "row" }} spacing={3} align={{ md: "flex-end" }} flexWrap="wrap">
        <FilterSelect
          label="Area"
          value={filters.area}
          onChange={setFilter("area")}
          anyLabel="Any area"
          options={AREAS.map((code) => [code, areaWords(code)])}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={setFilter("status")}
          anyLabel="Any status"
          options={STATUSES.map((code) => [code, statusWords(code)])}
        />
        <FilterSelect label="Role" value={filters.role} onChange={setFilter("role")} anyLabel="Any role" options={ROLES} />
        <FilterInput label="Action" value={filters.action} onChange={setFilter("action")} placeholder="e.g. order_assign" />
        <FilterInput label="Clinic schema" value={filters.schema} onChange={setFilter("schema")} placeholder="e.g. CLINIC_A" />
        <FilterInput label="From (UTC)" type="date" value={filters.since} onChange={setFilter("since")} />
        <FilterInput label="To (UTC)" type="date" value={filters.until} onChange={setFilter("until")} />
        <Button
          size="sm"
          onClick={() => setRefreshes((count) => count + 1)}
          isLoading={loading && !!data}
          flexShrink={0}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert status="error" borderRadius="md"><AlertIcon /><Text fontSize="sm">{error}</Text></Alert>
      )}
      {data?.truncated && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            More than {GROUPING_CAP.toLocaleString("en-US")} entries match these filters, so only the
            newest {GROUPING_CAP.toLocaleString("en-US")} are grouped. Narrow the filters to see the rest.
          </Text>
        </Alert>
      )}
      {!data && loading && !error && <Spinner />}
      {data && !groups.length && (
        <Alert status="info" borderRadius="md"><AlertIcon /><Text fontSize="sm">No gaps recorded for these filters.</Text></Alert>
      )}
      {groups.length > 0 && (
        <GroupsTable
          groups={groups}
          openKey={openKey}
          onToggle={setOpenKey}
          filters={applied}
          asOf={asOf}
          refreshes={refreshes}
          onMarked={load}
          taskByRef={taskByRef}
          canAssign={Boolean(me)}
          onAssign={assign}
          onResolve={resolveGroup}
        />
      )}
      {assigning && me && (() => {
        const { title, description } = gapTaskText(assigning.group, assigning.entry);
        const ref = assigning.entry ? assigning.entry.id : assigning.group.group_key;
        return (
          <AssignModal isOpen onClose={() => setAssigning(null)} currentUser={me} taskOnly
                       initialTitle={title} initialDescription={description}
                       taskSource={{ source_kind: SOURCE_KIND, source_ref: ref }}
                       onAssigned={reloadTasks} />
        );
      })()}
    </Stack>
  );
}

export default function AgentGapsModal({ isOpen, onClose }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Agent gaps</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <GapsPanel />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
